import {multipleDigitsNumberFormatter} from '@cypro/ui/utils'

/** the prescribing values a doctor wrote, either from the medicine log or the doctor-validation snapshot */
export interface DoctorChoiceValues {
  prescribedNameId?: string
  activeSubstanceId?: string
  totalAmount?: number
}

/** the medicine-row fields the doctor's choice is read from or falls back to */
export interface DoctorChoiceMedicine {
  doctorPrescribedAt?: number | null
  doctorPrescribedNameId?: string | null
  doctorPrescribedActiveSubstanceId?: string | null
  doctorPrescribedTotalAmount?: number | null
  prescribedNameId?: string | null
  activeSubstanceId?: string | null
  totalAmount?: number | null
}

/**
 * Picks what the doctor released for one medicine. The doctor-validation snapshot wins so a
 * later pharmacist change cannot rewrite it; rows validated before the snapshot existed fall
 * back to the doctor's prescribing log, then to the current row.
 */
export function resolveDoctorChoice(
  medicine: DoctorChoiceMedicine,
  logged: DoctorChoiceValues = {}
): DoctorChoiceValues {
  const snapshotted = Boolean(medicine.doctorPrescribedAt)

  return {
    // a null snapshot name means the doctor prescribed the substance, so it must not fall back to the row
    prescribedNameId: snapshotted
      ? (medicine.doctorPrescribedNameId ?? undefined)
      : (logged.prescribedNameId ?? medicine.prescribedNameId ?? undefined),
    activeSubstanceId: snapshotted
      ? (medicine.doctorPrescribedActiveSubstanceId ??
        logged.activeSubstanceId ??
        medicine.activeSubstanceId ??
        undefined)
      : (logged.activeSubstanceId ?? medicine.activeSubstanceId ?? undefined),
    totalAmount: snapshotted
      ? (medicine.doctorPrescribedTotalAmount ?? logged.totalAmount ?? medicine.totalAmount ?? undefined)
      : (logged.totalAmount ?? medicine.totalAmount ?? undefined),
  }
}

export interface DoctorPrescribedDisplay {
  name: string
  isTradeName: boolean
  /** e.g. "240 mg" — only for the active-substance variant (a trade name's dose is its own) */
  dosage: string
}

export function formatDoctorPrescribed(
  choice: DoctorChoiceValues,
  lookups: {tradeName?: {name?: string}; substance?: {name?: string; doseUnit?: string}}
): DoctorPrescribedDisplay {
  const isTradeName = Boolean(choice.prescribedNameId)

  if (isTradeName) return {name: lookups.tradeName?.name ?? '', isTradeName, dosage: ''}

  const totalAmount = choice.totalAmount ?? 0
  const dosage =
    totalAmount > 0
      ? `${multipleDigitsNumberFormatter.format(totalAmount)} ${lookups.substance?.doseUnit ?? ''}`.trim()
      : ''

  return {name: lookups.substance?.name ?? '', isTradeName, dosage}
}
